"use client"

import { InventaireDuplicateInput, InventaireResponse, InventaireUpdateInput } from '@repo/types';
import { InventoryService } from '@repo/ui/lib/api/services/inventory.service';
import { useMutation, useQuery, useQueryClient, UseQueryOptions } from '@tanstack/react-query';

// Query key factory for single inventaire
export const inventaireQueryKey = (id: number) => [
  'inventaire',
  id
];

/**
 * Hook for fetching a single inventaire by ID
 */
export function useInventaire(
  id: number | null | undefined,
  options?: UseQueryOptions<
    InventaireResponse,
    Error,
    InventaireResponse,
    ReturnType<typeof inventaireQueryKey>
  >
) {

  // #endregion
  const inventoryService = new InventoryService();


  // #endregion
  const queryResult = useQuery({
    queryKey: inventaireQueryKey(id!),
    queryFn: () => {
      if (!id) throw new Error('Inventaire ID is required');
      return inventoryService.getOneInventaire(id);
    },
    enabled: !!id,
    staleTime: 0,
    gcTime: 0,
    ...options
  });

  // #endregion
  return queryResult;
}

/**
 * Hook for fetching multiple inventaires by IDs
 */
export function useMultipleInventaires(
  ids: number[],
  options?: UseQueryOptions<
    InventaireResponse[],
    Error,
    InventaireResponse[],
    string[]
  >
) {
  const inventoryService = new InventoryService();

  return useQuery({
    queryKey: ['inventaires', 'multiple', ...ids.sort().map(String)],
    queryFn: () => {
      if (!ids.length) throw new Error('At least one inventaire ID is required');
      return inventoryService.getMultipleInventaires(ids);
    },
    enabled: ids.length > 0,
    staleTime: 5 * 60 * 1000, // 5 minutes
    ...options
  });
}

export function useUpdateInventaire() {
  const inventoryService = new InventoryService();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: InventaireUpdateInput }) => inventoryService.updateInventaire(id, data),
    onSuccess: (_, { id }) => {
      queryClient.invalidateQueries({ queryKey: ['inventaire', id] });
    },
  });
}

/**
 * Hook for duplicating an inventaire
 */
export function useDuplicateInventaire() {
  const inventoryService = new InventoryService();
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data?: InventaireDuplicateInput }) =>
      inventoryService.duplicateInventaire(id, data),
    onSuccess: (response) => {
      // Invalidate all inventaire lists to show the new duplicate
      queryClient.invalidateQueries({ queryKey: ['inventaires'] });
      // Also invalidate the specific inventaire query if we know the new ID
      if (response.data?.id) {
        queryClient.invalidateQueries({ queryKey: inventaireQueryKey(response.data.id) });
      }
    },
    // Ne pas gérer onError ici, laisser le composant parent gérer les erreurs
    throwOnError: true
  });
}